import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
// mantine
import { Button, TextInput, UnstyledButton } from '@mantine/core';
import type {
    IIntegration,
    ISettingPreset,
    IConnectorData
} from '@newo/api/gateway';
import yup from '@/libs/yup.ts';
// components
import SettingField from '@/modules/Integrations/components/SettingField';
// utils
import {
    buildFormFromPreset,
    isSettingVisible,
    pickVisibleSettings
} from '@/pages/Integrations/Integrations.utils.ts';
import {
    QUERY_KEYS,
    useCreateAndRunConnectorMutation
} from '@/services/integration/integration.queries.ts';

interface AddConnectorFormProps {
    isDisabled?: boolean;
    integration: IIntegration;
    presets: ISettingPreset[];
    onCancel?: () => void;
    onSubmit?: () => void;
}

interface FormikInitialValues {
    title: string;
    connector_idn: string;
    settings: Record<string, unknown>;
}

const AddConnectorForm = ({
    isDisabled = false,
    integration,
    presets,
    onCancel,
    onSubmit
}: AddConnectorFormProps) => {
    const queryClient = useQueryClient();
    const mutation = useCreateAndRunConnectorMutation();
    const {
        initialValues: settingsInitialValues,
        validationSchema: settingsValidationSchema
    } = buildFormFromPreset(presets);
    const initialValues: FormikInitialValues = {
        title: '',
        connector_idn: '',
        settings: settingsInitialValues
    };

    const formik = useFormik({
        initialValues,
        validationSchema: yup.object({
            title: yup.string().trim().required('Title is a required field'),
            connector_idn: yup.string().required().idn(),
            settings: settingsValidationSchema
        }),
        onSubmit(values) {
            const data: IConnectorData = {
                title: values.title.trim(),
                connector_idn: values.connector_idn,
                settings: Object.entries(
                    pickVisibleSettings(presets, values.settings)
                ).map(([idn, value]) => ({
                    idn,
                    value
                }))
            };
            mutation.mutate(
                {
                    integration,
                    data
                },
                {
                    onSuccess() {
                        queryClient.invalidateQueries({
                            queryKey: QUERY_KEYS.getIntegrationConnectors(
                                integration.id
                            )
                        });
                        onSubmit?.();
                    },
                    onSettled() {
                        formik.setSubmitting(false);
                    }
                }
            );
        }
    });

    const handleCancel = () => {
        onCancel?.();
    };

    return (
        <form
            autoComplete="off"
            className={clsx(
                'flex flex-col gap-3',
                isDisabled &&
                    'pointer-events-none cursor-not-allowed opacity-50'
            )}
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                error={formik.touched.title ? formik.errors.title : undefined}
                label="Title"
                name="title"
                placeholder="Connector title"
                value={formik.values.title}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <TextInput
                withAsterisk
                description="Unique connector name"
                error={
                    formik.touched.connector_idn
                        ? formik.errors.connector_idn
                        : undefined
                }
                label="Idn"
                name="connector_idn"
                placeholder="Connector name"
                value={formik.values.connector_idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            {presets
                .filter((control) =>
                    isSettingVisible(control, formik.values.settings)
                )
                .map((control) => (
                    <SettingField
                        control={control}
                        error={formik.errors.settings?.[control.idn]}
                        key={control.idn}
                        value={formik.values.settings[control.idn] as string}
                        onChange={(value) => {
                            formik.setFieldValue(
                                `settings.${control.idn}`,
                                value
                            );
                        }}
                    />
                ))}
            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    Save
                </Button>
            </div>
        </form>
    );
};

export default AddConnectorForm;
